import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import useAuth from '../../../../hooks/useAuth'
import { getSessionHistory } from '../../SellerQueue/services'
import { SellerServiceSessionItem } from '../../SellerQueue/types'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

type FollowUpPriority = 'hoje' | '24h+' | '48h+'

interface FollowUpItem {
  sessionId: number
  sellerId: number
  sellerLabel: string
  startedAt: string
  finishedAt: string | null
  ageHours: number
  ageLabel: string
  priority: FollowUpPriority
  action: string
  responseSla: string
}

interface SellerFollowUpSummary {
  sellerId: number
  sellerLabel: string
  total: number
  todayCount: number
  plus24Count: number
  plus48Count: number
  avgAgeHours: number
}

const numberFormatter = new Intl.NumberFormat('pt-BR')
const hourFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const parseDate = (value?: string | null): Date | null => {
  if (!value) {
    return null
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date
}

const getSellerLabel = (session: SellerServiceSessionItem): string => {
  const fallbackName = `Vendedor ${session.sellerId}`
  const rawName = session.sellerName?.trim()

  return rawName && rawName.length > 0 ? rawName : fallbackName
}

const formatAgeLabel = (hours: number): string => {
  const totalMinutes = Math.max(0, Math.round(hours * 60))
  const dayCount = Math.floor(totalMinutes / 1440)
  const hourCount = Math.floor((totalMinutes % 1440) / 60)
  const minuteCount = totalMinutes % 60

  if (dayCount > 0) {
    return `${dayCount}d ${hourCount}h`
  }

  if (hourCount > 0) {
    return `${hourCount}h ${String(minuteCount).padStart(2, '0')}m`
  }

  return `${minuteCount}m`
}

const getPriority = (ageHours: number): FollowUpPriority => {
  if (ageHours >= 48) {
    return '48h+'
  }

  if (ageHours >= 24) {
    return '24h+'
  }

  return 'hoje'
}

const getActionByPriority = (priority: FollowUpPriority): string => {
  if (priority === '48h+') {
    return 'Acao imediata: contato ativo + escalonamento para gestao comercial.'
  }

  if (priority === '24h+') {
    return 'Contato prioritario com proposta objetiva e tentativa de fechamento.'
  }

  return 'Checklist do dia: lembrete de follow-up e confirmacao de interesse.'
}

const getResponseSla = (priority: FollowUpPriority): string => {
  if (priority === '48h+') {
    return 'Imediato'
  }

  if (priority === '24h+') {
    return 'Ate 2h'
  }

  return 'Hoje'
}

const getPriorityBadgeClass = (priority: FollowUpPriority): string => {
  if (priority === '48h+') {
    return 'badge badge-light-danger'
  }

  if (priority === '24h+') {
    return 'badge badge-light-warning'
  }

  return 'badge badge-light-primary'
}

const exportToCSV = (filename: string, data: any[]): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Duração (h);Prioridade;Ação;SLA Resposta\n'

  data.forEach((row, index) => {
    csv += `${index + 1};"${row.sellerLabel}";${row.ageHours.toFixed(1)};"${row.priority}";"${row.action.replace(/"/g, '""')}";"${row.responseSla}"\n`
  })

  const bom = '\uFEFF'
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, data: any[]): void => {
  const rows = data.map((row, index) => ({
    'Posição': index + 1,
    'Vendedor': row.sellerLabel,
    'Duração (h)': row.ageHours.toFixed(1),
    'Prioridade': row.priority,
    'SLA Resposta': row.responseSla,
  }))

  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Follow-up')
  XLSX.writeFile(wb, filename)
}

const exportToPDF = (filename: string, data: any[], summary: any): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  // Header
  pdf.setFontSize(16)
  pdf.text('Fila de Follow-up - Pending Checkout', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  const reportDate = new Date().toLocaleString('pt-BR')
  pdf.text(`Relatório gerado em: ${reportDate}`, margin, yPosition)
  yPosition += 12

  // Summary Section
  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores da Fila', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  const summaryText = [
    `Total na Fila: ${summary.total} | Hoje: ${summary.todayCount} | 24h+: ${summary.plus24Count} | 48h+: ${summary.plus48Count}`,
    `Aging Médio: ${summary.avgAgeHours.toFixed(1)}h`,
  ]

  summaryText.forEach((line) => {
    pdf.text(line, margin, yPosition)
    yPosition += 5
  })

  yPosition += 5

  // Follow-up Table
  const tableData = data.slice(0, 50).map((row, index) => [
    index + 1,
    row.sellerLabel,
    row.ageHours.toFixed(1),
    row.priority,
    row.responseSla,
  ])

  autoTable(pdf, {
    startY: yPosition,
    head: [['Pos', 'Vendedor', 'Duração (h)', 'Prioridade', 'SLA Resposta']],
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: 50,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      1: { halign: 'left' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
    },
  })

  // Footer
  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(
      `Página ${i} de ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    )
  }

  pdf.save(filename)
}

const SellerPendingCheckoutFollowUpReportPage: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = Number(loginInfo.pluginspaceId || 0)

  const [filters, setFilters] = useState({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
  })
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SellerServiceSessionItem[]>([])

  const loadReport = useCallback(async () => {
    if (!pluginspaceId || pluginspaceId <= 0) {
      setSessions([])
      return
    }

    try {
      setLoading(true)
      const data = await getSessionHistory(pluginspaceId, filters.startDate, filters.finalDate)
      setSessions(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatorio',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters.finalDate, filters.startDate, pluginspaceId])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const followUpQueue = useMemo<FollowUpItem[]>(() => {
    const now = Date.now()

    return sessions
      .filter((session) => session.outcome === 'pending_checkout')
      .map((session) => {
        const referenceDate = parseDate(session.finishedAt) || parseDate(session.startedAt)
        const ageHours = referenceDate ? Math.max(0, (now - referenceDate.getTime()) / 3600000) : 0
        const priority = getPriority(ageHours)

        return {
          sessionId: session.id,
          sellerId: session.sellerId,
          sellerLabel: getSellerLabel(session),
          startedAt: session.startedAt,
          finishedAt: session.finishedAt,
          ageHours,
          ageLabel: formatAgeLabel(ageHours),
          priority,
          action: getActionByPriority(priority),
          responseSla: getResponseSla(priority),
        }
      })
      .sort((left, right) => {
        const priorityWeight = { '48h+': 3, '24h+': 2, hoje: 1 }
        if (priorityWeight[right.priority] !== priorityWeight[left.priority]) {
          return priorityWeight[right.priority] - priorityWeight[left.priority]
        }

        return right.ageHours - left.ageHours
      })
  }, [sessions])

  const summary = useMemo(() => {
    const total = followUpQueue.length
    const todayCount = followUpQueue.filter((item) => item.priority === 'hoje').length
    const plus24Count = followUpQueue.filter((item) => item.priority === '24h+').length
    const plus48Count = followUpQueue.filter((item) => item.priority === '48h+').length

    return {
      total,
      todayCount,
      plus24Count,
      plus48Count,
    }
  }, [followUpQueue])

  const sellerSummary = useMemo<SellerFollowUpSummary[]>(() => {
    const grouped = new Map<number, SellerFollowUpSummary>()

    followUpQueue.forEach((item) => {
      const current = grouped.get(item.sellerId)
      if (current) {
        current.total += 1
        current.avgAgeHours += item.ageHours

        if (item.priority === 'hoje') current.todayCount += 1
        if (item.priority === '24h+') current.plus24Count += 1
        if (item.priority === '48h+') current.plus48Count += 1
        return
      }

      grouped.set(item.sellerId, {
        sellerId: item.sellerId,
        sellerLabel: item.sellerLabel,
        total: 1,
        todayCount: item.priority === 'hoje' ? 1 : 0,
        plus24Count: item.priority === '24h+' ? 1 : 0,
        plus48Count: item.priority === '48h+' ? 1 : 0,
        avgAgeHours: item.ageHours,
      })
    })

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        avgAgeHours: row.total ? row.avgAgeHours / row.total : 0,
      }))
      .sort((left, right) => right.plus48Count - left.plus48Count || right.plus24Count - left.plus24Count || right.total - left.total)
  }, [followUpQueue])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Follow-up Backlog por Vendedor',
            path: '/seller/reports/pending-checkout-followup',
            isActive: true,
          },
        ]}
      >
        Relatorio de Follow-up Backlog
      </PageTitle>

      <ReportShell
        title='Follow-up Backlog por Vendedor'
        description='Fila de ação para pending checkout priorizada por urgência (hoje, 24h+, 48h+).'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button
              className='btn btn-light-success'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToCSV(`FollowUp-Backlog-${timestamp}.csv`, followUpQueue)
              }}
              disabled={loading || followUpQueue.length === 0}
              title='Baixar fila em CSV'
            >
              <i className='fas fa-file-csv me-2'></i>
              CSV
            </button>
            <button
              className='btn btn-light-info'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToXLSX(`FollowUp-Backlog-${timestamp}.xlsx`, followUpQueue)
              }}
              disabled={loading || followUpQueue.length === 0}
              title='Baixar fila em XLSX'
            >
              <i className='fas fa-file-excel me-2'></i>
              XLSX
            </button>
            <button
              className='btn btn-light-danger'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToPDF(`FollowUp-Backlog-${timestamp}.pdf`, followUpQueue, summary)
              }}
              disabled={loading || followUpQueue.length === 0}
              title='Baixar fila em PDF'
            >
              <i className='fas fa-file-pdf me-2'></i>
              PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        {!pluginspaceId ? (
          <div className='alert alert-warning mb-0'>Pluginspace não identificado para o usuário logado.</div>
        ) : (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Pendências totais</div>
                    <div className='fs-2hx fw-bold text-warning'>{numberFormatter.format(summary.total)}</div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Hoje</div>
                    <div className='fs-2hx fw-bold text-primary'>{numberFormatter.format(summary.todayCount)}</div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Prioridade 24h+</div>
                    <div className='fs-2hx fw-bold text-warning'>{numberFormatter.format(summary.plus24Count)}</div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Crítico 48h+</div>
                    <div className='fs-2hx fw-bold text-danger'>{numberFormatter.format(summary.plus48Count)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm mb-4'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Resumo por vendedor</span>
                  <span className='text-gray-600 mt-1'>Backlog priorizado por urgencia de follow-up.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : sellerSummary.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Posicao</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Total</th>
                          <th className='text-end'>Hoje</th>
                          <th className='text-end'>24h+</th>
                          <th className='text-end'>48h+</th>
                          <th className='text-end'>Idade media</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerSummary.map((row, index) => (
                          <tr key={row.sellerId}>
                            <td>#{index + 1}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td className='text-end'>{numberFormatter.format(row.total)}</td>
                            <td className='text-end'>{numberFormatter.format(row.todayCount)}</td>
                            <td className='text-end text-warning fw-semibold'>{numberFormatter.format(row.plus24Count)}</td>
                            <td className='text-end text-danger fw-semibold'>{numberFormatter.format(row.plus48Count)}</td>
                            <td className='text-end'>{hourFormatter.format(row.avgAgeHours)}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>Nenhum pending checkout encontrado para os filtros selecionados.</div>
                )}
              </div>
            </div>

            <div className='card border-0 shadow-sm'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Fila priorizada de ação</span>
                  <span className='text-gray-600 mt-1'>Ordenado por prioridade e idade para execução operacional do dia.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : followUpQueue.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Prioridade</th>
                          <th>Sessao</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Aging</th>
                          <th className='text-end'>SLA de resposta</th>
                          <th>Acao sugerida</th>
                        </tr>
                      </thead>
                      <tbody>
                        {followUpQueue.map((item) => (
                          <tr key={item.sessionId}>
                            <td>
                              <span className={getPriorityBadgeClass(item.priority)}>{item.priority}</span>
                            </td>
                            <td>#{item.sessionId}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={item.sellerLabel} />
                            </td>
                            <td className='text-end fw-semibold'>{item.ageLabel}</td>
                            <td className='text-end'>{item.responseSla}</td>
                            <td>{item.action}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>Nenhum pending checkout encontrado para os filtros selecionados.</div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportShell>
    </>
  )
}

export { SellerPendingCheckoutFollowUpReportPage }
